import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useCallback, useState } from 'react'
import { useOnboardingStepViewed } from '@/hooks/use-analytics'
import { useOnboarding } from '@/hooks/use-onboarding'
import { StepLayout } from './-components/step-layout'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Plus, Trash2, Users } from 'lucide-react'

export const Route = createFileRoute('/onboarding/team')({
  component: TeamStep,
})

function TeamStep() {
  useOnboardingStepViewed('team')
  const navigate = useNavigate()
  const { teamInvites, updateState } = useOnboarding()

  const [emails, setEmails] = useState<string[]>(
    teamInvites.length > 0 ? teamInvites : [''],
  )

  const addField = useCallback(() => {
    setEmails(prev => [...prev, ''])
  }, [])

  const removeField = useCallback((i: number) => {
    setEmails(prev => prev.filter((_, idx) => idx !== i))
  }, [])

  const updateField = useCallback((i: number, value: string) => {
    setEmails((prev) => {
      const updated = [...prev]
      updated[i] = value
      return updated
    })
  }, [])

  const validEmails = emails.filter(e => e.includes('@'))

  const handleNext = () => {
    updateState({ teamInvites: validEmails, currentStep: 6 })
    navigate({ to: '/onboarding/partners' })
  }

  const handleBack = () => {
    navigate({ to: '/onboarding/claim-territory' })
  }

  return (
    <StepLayout
      title="Invite your team"
      subtitle="Add colleagues who should join you on SUPPLYCO. They'll be able to claim their own territories."
      onNext={handleNext}
      onBack={handleBack}
      nextLabel={validEmails.length > 0 ? `Send ${validEmails.length} invite${validEmails.length > 1 ? 's' : ''}` : 'Continue'}
      maxWidth="md"
    >
      <div className="space-y-6">
        <div className="flex items-center gap-3 rounded-lg border bg-muted/30 p-4">
          <Users className="h-5 w-5 shrink-0 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">
            Inviting team members now lets them claim territories and connect with partners right away.
          </p>
        </div>

        <div className="space-y-3">
          {emails.map((email, i) => (
            <div key={i} className="flex items-center gap-2">
              <Input
                type="email"
                placeholder="colleague@company.com"
                value={email}
                onChange={e => updateField(i, e.target.value)}
                autoFocus={i === 0 && email === ''}
              />
              {emails.length > 1 && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => removeField(i)}
                  className="shrink-0"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </div>
          ))}

          <button
            type="button"
            onClick={addField}
            className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary"
          >
            <Plus className="h-3.5 w-3.5" />
            Add another team member
          </button>
        </div>
      </div>
    </StepLayout>
  )
}
